'use client';

import { useState } from 'react';

import NextImage, { ImageProps } from 'next/image';

import imageLoader from '@/lib/utils/cloudinaryLoader';

interface CloudinaryImageProps extends Omit<ImageProps, 'loader'> {
  preload?: boolean;
  alt: string;
  title?: string;
  format?: string;
  fallbackSrc?: string;
}

export default function CloudinaryImage({
  preload: _preload,
  alt,
  title,
  src,
  fallbackSrc = '/images/placeholder-image.jpg',
  ...props
}: CloudinaryImageProps) {
  const [isUsingCloudinary, setIsUsingCloudinary] = useState(true);
  const [isUsingFallback, setIsUsingFallback] = useState(false);
  const [imgSrc, setImgSrc] = useState<typeof src>(src);

  const getSourceString = (source: typeof src): string => {
    if (typeof source === 'string') return source;
    if ('src' in source) return source.src;
    return fallbackSrc;
  };

  // Handle images that fail to load
  const handleImageError = () => {
    if (isUsingCloudinary) {
      // First try direct image path if Cloudinary fails
      setIsUsingCloudinary(false);
      setImgSrc(getSourceString(src));
    } else if (!isUsingFallback) {
      // If direct path also fails, use fallback
      setIsUsingFallback(true);
      setImgSrc(fallbackSrc);
    }
  };

  return (
    <NextImage
      {...props}
      src={isUsingFallback ? fallbackSrc : imgSrc}
      alt={alt}
      title={title}
      loader={isUsingCloudinary ? imageLoader : undefined}
      onError={handleImageError}
      unoptimized={isUsingFallback} // Don't optimize fallback images
    />
  );
}
